import clsx from "clsx";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Seomatic } from "react-seomatic";

import ColumnLayout from "@components/ColumnLayout";
import ContentSections from "@components/ContentSections";
import Heading from "@components/Heading";
import SidebarCalloutsComponent from "@components/SidebarCallouts";
import useSetHeaderTheme from "@hooks/useSetHeaderTheme";
import useSetIsHeaderFixed from "@hooks/useSetIsHeaderFixed";
import { PreviewContent } from "@interfaces/PreviewContent.interface";
import { returnStaticProps } from "@lib/returnStaticProps";
import craft from "@pages/api/craft";
import QuerySlugs from "@queries/QuerySlugs.graphql";
import QueryTour from "@queries/tours/QueryTour.graphql";
import Iframe from "react-iframe";

import { PLASMIC } from "@abnormal/plasmic-init";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink
} from "@chakra-ui/react";
import { headerHeight } from "@lib/headerHeight";
import getHref from "@lib/routes";
import {
  ComponentRenderData,
  PlasmicComponent,
  PlasmicRootProvider,
  extractPlasmicQueryData
} from "@plasmicapp/loader-nextjs";
import Image from "next/image";

export const getStaticPaths: GetStaticPaths = async (context) => {
	// Fetch every possible path
	const response = await craft(QuerySlugs, {
		section: "tours",
		limit: 20
	});

	const paths = [];

	response.entries.map(async (tour) => {
		console.log(`Generating /tours/${tour.slug}`);

		paths.push({
			params: { slug: tour.slug }
		});
	});

	return { paths, fallback: "blocking" };
};



export const getStaticProps: GetStaticProps = async ({
	params,
	previewData: nextPreviewData,
	preview
}) => {
	// For Craft preview capability
	const previewData: PreviewContent = nextPreviewData as PreviewContent;

	// Construct uri from the slug array
	const { slug } = params;

	// What is the uri
	const uri = `/tours/${slug}`;

  const plasmicData = await PLASMIC.maybeFetchComponentData(["BladesDynamicDemoBlade", "Footer"], {
    // deferChunks: true,
  });
	if (!plasmicData) {
		throw new Error("No Plasmic design found");
	}

	const compMeta = plasmicData.entryCompMetas[0];

	// Cache the necessary data fetched for the page
	const queryCache = await extractPlasmicQueryData(
		<PlasmicRootProvider
			loader={PLASMIC}
			prefetchedData={plasmicData}
			pageParams={compMeta.params}
		>
			<PlasmicComponent component={compMeta.displayName} />
		</PlasmicRootProvider>
	);

	// Fetch the page's data
	const response = await craft(
		QueryTour,
		{ slug },
		preview ? previewData.token : null
	);

	const {
		pageEntry,
		tour,
		primaryNavigation,
		primaryCallToActions,
		persistentNav
	} = response;

	// Return props if entry exists, otherwise check Retour for redirects or 404
	const returnResponse = await returnStaticProps(
		{
			navigation: {
				primaryNavigation,
				primaryCallToActions
			},
			pageEntry,
			persistentNav,
			tour,
			plasmicData,
			queryCache
		},
		!tour || (pageEntry.status == "disabled" && !preview),
		uri
	);

	return returnResponse;
};

interface IProps {
	pageEntry: any;
	tour: any;
	plasmicData: ComponentRenderData;
	queryCache: Record<string, any>;
}

export default function Page({
	pageEntry,
	tour,
	plasmicData,
	queryCache
}: IProps): JSX.Element {
	useSetHeaderTheme("dark");
	useSetIsHeaderFixed(false);

	const router = useRouter();

	const breadcrumbHref = getHref(pageEntry);

	const compMeta = plasmicData.entryCompMetas[0];

	const overlayTextTheme = tour.tourTypes[0]?.overlayTextTheme
		? tour.tourTypes[0].overlayTextTheme
		: "light";

	return (
		<>
			<Seomatic Head={Head} {...tour.seomatic} />
			{/* Entry Header */}
			<Box
				className={clsx("tourHeader__wrapper", "py-12", "px-5", "w-full", {
					"text-white-01": overlayTextTheme === "light"
				})}
				backgroundColor={tour.tourTypes[0]?.backgroundColor}
				// mt={`${headerHeight}px`}
			>
				<Box className="flex items-center mx-auto" maxW="1200px">
					<div className="mr-6">
						{tour.heroImage[0] && (
							<Image
								src={tour.heroImage[0].url}
								height={96}
								width={96}
								alt={tour.title}
								className={clsx("EntryThumbnail__image")}
								draggable={false}
								blurDataURL={tour.heroImage[0].url}
							/>
						)}
					</div>
					<div>
						<div className={clsx("EntryHeader__breadcrumb", "mb-3")}>
							<Breadcrumb>
								<BreadcrumbItem>
									<BreadcrumbLink href={breadcrumbHref}>
										{pageEntry.title}
									</BreadcrumbLink>
								</BreadcrumbItem>
								<BreadcrumbItem hidden={!tour.tourTypes.length}>
									<BreadcrumbLink
										href={`${breadcrumbHref}#${tour.tourTypes[0]?.slug}`}
									>
										{tour.tourTypes[0]?.title}
									</BreadcrumbLink>
								</BreadcrumbItem>
								<BreadcrumbItem isCurrentPage hidden>
									<BreadcrumbLink href={tour.url}>{tour.title}</BreadcrumbLink>
								</BreadcrumbItem>
							</Breadcrumb>
						</div>
						<Heading size="h1" mb={0} className="tourHeader__title">
							{tour.title}
						</Heading>
					</div>
				</Box>
			</Box>
			{/* Tour Frame */}
			<Box className="py-12">
				<Box
					className="mx-auto"
					maxW="1402px"
					borderWidth="1px"
					borderColor="rgba(13, 20, 24, 0.3)"
				>
					<Box
						className="mx-auto"
						width="1400px"
						maxW="100%"
						maxH="789.5px"
						pb="56.25%"
						position="relative"
					>
						<Iframe
							url={tour.externalUrl}
							width="100%"
							height="100%"
							position="absolute"
						/>
					</Box>
				</Box>
			</Box>
			{/* Page Content */}
			<ColumnLayout>
				<div className="flex flex-col-reverse md:flex-row w-full">
					<ColumnLayout.MainColumn className="mx-auto pb-15">
						<ContentSections sections={tour.contentSections} />
					</ColumnLayout.MainColumn>
					<ColumnLayout.SidebarColumn className={clsx("md:pb-15 relative")}>
						<div className="sticky flex flex-col top-[100px] gap-5">
              <SidebarCalloutsComponent
                  sidebarCallouts={tour.sidebarCallouts}
                  isTour={true}
                />
						</div>
					</ColumnLayout.SidebarColumn>
				</div>
			</ColumnLayout>
			<PlasmicRootProvider
				loader={PLASMIC}
				prefetchedData={plasmicData}
				prefetchedQueryData={queryCache}
				pageParams={compMeta.params}
				pageQuery={router.query}
			>
			<PlasmicComponent
        component={compMeta.displayName}
        componentProps={{
          craftVersion: true,
          customCtaText: "Get a Live Demo",
          backgroundColor: "#EEEEF9"
        }}
      />
			</PlasmicRootProvider>
		</>
	);
}
